import {
  CategoriaCupon,
  CategoriaCuponFormValue,
  CuponDescuento,
  CuponFormValue,
  DiaSemana,
  Descuento,
  DescuentoFormValue,
  Oferta,
  OfertaFormValue,
  OfertaPrecioBajo,
  PeriodoOferta,
  PrecioBajoFormValue,
  SucursalDescuento,
  SucursalOferta,
  TipoOferta,
} from '../models/promociones.model';
import {
  BranchSelectionDto,
  CouponCategoryRequestDto,
  CouponCategoryResponseDto,
  CouponRequestDto,
  CouponResponseDto,
  DiscountRequestDto,
  DiscountResponseDto,
  LowPriceRequestDto,
  LowPriceResponseDto,
  OfferRequestDto,
  OfferResponseDto,
  PeriodDto,
  PromoTypeDto,
} from './promotions-api.types';

function sinSegundos(valor: string | null): string {
  if (!valor) return '';
  return valor.length > 16 && valor.includes('T')
    ? valor.slice(0, 16)
    : valor.length > 5 && !valor.includes('T')
      ? valor.slice(0, 5)
      : valor;
}

function aNumero(valor: string | number | null | undefined): number {
  const numero = typeof valor === 'number' ? valor : Number.parseFloat(String(valor ?? ''));
  return Number.isFinite(numero) ? numero : 0;
}

const DIAS_A_ISO: Record<DiaSemana, number> = { L: 1, M: 2, X: 3, J: 4, V: 5, S: 6, D: 7 };
const ISO_A_DIAS: Record<number, DiaSemana> = { 1: 'L', 2: 'M', 3: 'X', 4: 'J', 5: 'V', 6: 'S', 7: 'D' };

export function tipoOfertaADto(tipo: TipoOferta): PromoTypeDto {
  return tipo === 'dos-en-uno' ? 'TWO_IN_ONE' : 'TWO_FOR_ONE';
}

export function dtoATipoOferta(tipo: PromoTypeDto): TipoOferta {
  return tipo === 'TWO_IN_ONE' ? 'dos-en-uno' : 'dos-por-uno';
}

export function periodoADto(periodo: PeriodoOferta): PeriodDto {
  const permanente = periodo.tipo === 'permanente';

  return {
    type: permanente ? 'PERMANENT' : 'FIXED',
    startsOn: permanente ? null : periodo.inicia || null,
    endsOn: permanente ? null : periodo.finaliza || null,
    startTime: permanente ? periodo.horaInicio || null : null,
    endTime: permanente ? periodo.horaFin || null : null,
    weekdays: permanente ? periodo.dias.map((d) => DIAS_A_ISO[d]) : [],
  };
}

export function dtoAPeriodo(dto: PeriodDto): PeriodoOferta {
  return {
    tipo: dto.type === 'PERMANENT' ? 'permanente' : 'determinado',
    inicia: sinSegundos(dto.startsOn),
    finaliza: sinSegundos(dto.endsOn),
    horaInicio: sinSegundos(dto.startTime),
    horaFin: sinSegundos(dto.endTime),
    dias: (dto.weekdays ?? []).map((n) => ISO_A_DIAS[n]).filter(Boolean),
  };
}

export function sucursalOfertaADto(sucursal: SucursalOferta): BranchSelectionDto {
  return {
    branchId: Number(sucursal.id),
    included: sucursal.incluida,
    ownPrice: sucursal.precioPropio,
    price: sucursal.precioPropio ? sucursal.precio : null,
  };
}

export function dtoASucursalOferta(dto: BranchSelectionDto): SucursalOferta {
  return {
    id: String(dto.branchId),
    nombre: dto.branchName ?? '',
    incluida: dto.included,
    precioPropio: dto.ownPrice,
    precio: aNumero(dto.price),
  };
}

export function sucursalDescuentoADto(sucursal: SucursalDescuento): BranchSelectionDto {
  return {
    branchId: Number(sucursal.id),
    included: sucursal.incluida,
    ownPrice: false,
    price: null,
  };
}

export function dtoASucursalDescuento(dto: BranchSelectionDto): SucursalDescuento {
  return {
    id: String(dto.branchId),
    nombre: dto.branchName ?? '',
    incluida: dto.included,
  };
}

export function descuentoADto(form: DescuentoFormValue): DiscountRequestDto {
  return {
    name: form.nombre,
    percentage: form.porcentaje,
    maxAmount: form.montoMaximo || null,
    active: form.activo,
    excludedCategoryIds: form.categoriasExcluidas.map((c) => Number(c.id)),
    branches: form.sucursales.map(sucursalDescuentoADto),
  };
}

export function dtoADescuento(
  dto: DiscountResponseDto,
  catalogoCategorias: { id: string; nombre: string; padre?: string; cantidadProductos: number }[],
): Descuento {
  const excluidas = dto.excludedCategoryIds.map(
    (id) =>
      catalogoCategorias.find((c) => c.id === String(id)) ?? {
        id: String(id),
        nombre: `Categoría ${id}`,
        cantidadProductos: 0,
      },
  );

  return {
    id: String(dto.id),
    nombre: dto.name,
    porcentaje: aNumero(dto.percentage),
    montoMaximo: aNumero(dto.maxAmount),
    activo: dto.active,
    categoriasExcluidas: excluidas,
    sucursales: dto.branches.map(dtoASucursalDescuento),
  };
}

export function categoriaCuponADto(form: CategoriaCuponFormValue): CouponCategoryRequestDto {
  return {
    description: form.descripcion,
    consumptionType: form.tipoConsumo === 'unico' ? 'SINGLE' : 'MULTIPLE',
    defaultExpiresOn: form.fechaExpiracionDefecto || null,
    active: form.activa,
  };
}

export function dtoACategoriaCupon(dto: CouponCategoryResponseDto): CategoriaCupon {
  return {
    id: String(dto.id),
    descripcion: dto.description,
    tipoConsumo: dto.consumptionType === 'SINGLE' ? 'unico' : 'multiple',
    fechaExpiracionDefecto: dto.defaultExpiresOn ?? '',
    activa: dto.active,
  };
}

export function cuponADto(form: CuponFormValue): CouponRequestDto {
  const porLotes = form.modoRegistro === 'lotes';

  return {
    categoryId: form.categoriaId ? Number(form.categoriaId) : null,
    expiresOn: form.fechaExpiracion || null,
    valueType: form.tipoDescuento === 'porcentaje' ? 'PERCENT' : 'FIXED',
    value: form.valorDescuento,
    maxAmount: form.montoMaximo || null,
    active: form.activo,
    registrationMode: porLotes ? 'BATCH' : 'CODE',
    code: porLotes ? null : form.codigo,
    batchPrefix: porLotes ? form.lote.sigla : null,
    batchFrom: porLotes ? aNumero(form.lote.numeroInicial) : null,
    batchTo: porLotes ? aNumero(form.lote.numeroFinal) : null,
    amountTotal: form.montoMaximo || 0,
    branches: form.sucursales.map(sucursalDescuentoADto),
    productIds: form.productos.map((p) => Number(p.id)),
  };
}

export function dtoACupon(
  dto: CouponResponseDto,
  catalogoProductos: { id: string; nombre: string; presentacion: string }[],
): CuponDescuento {
  const productos = dto.productIds.map(
    (id) =>
      catalogoProductos.find((p) => p.id === String(id)) ?? {
        id: String(id),
        nombre: `Producto ${id}`,
        presentacion: '',
      },
  );

  return {
    id: String(dto.id),
    codigo: dto.code ?? dto.batchPrefix ?? '',
    cantidadLote: dto.batchSize ?? undefined,
    modoRegistro: dto.registrationMode === 'BATCH' ? 'lotes' : 'codigo',
    lote:
      dto.registrationMode === 'BATCH'
        ? {
            sigla: dto.batchPrefix ?? '',
            numeroInicial: String(dto.batchFrom ?? ''),
            numeroFinal: String(dto.batchTo ?? ''),
          }
        : undefined,
    categoriaId: dto.categoryId ? String(dto.categoryId) : '',
    categoriaNombre: dto.categoryName ?? '',
    tipoDescuento: dto.valueType === 'PERCENT' ? 'porcentaje' : 'monto',
    valorDescuento: aNumero(dto.value),
    montoMaximo: aNumero(dto.maxAmount),
    montoDisponible: aNumero(dto.amountAvailable),
    montoUsado: aNumero(dto.amountUsed),
    fechaExpiracion: dto.expiresOn ?? '',
    activo: dto.active,
    sucursales: dto.branches.map(dtoASucursalDescuento),
    productos,
  };
}

export function ofertaADto(form: OfertaFormValue, tipo: TipoOferta): OfferRequestDto {
  const dosEnUno = tipo === 'dos-en-uno';

  return {
    period: periodoADto(form.periodo),
    maxQuantity: form.cantidadMaxima,
    stock: form.stock,
    products: form.productos.map((p) => ({
      productId: Number(p.id),
      presentationId: null,
      productName: p.nombre,
      buyQuantity: p.compra,
      offerPrice: dosEnUno ? p.valor : null,
      getQuantity: dosEnUno ? null : p.valor,
    })),
    branches: form.sucursales.map(sucursalOfertaADto),
  };
}

export function dtoAOferta(dto: OfferResponseDto): Oferta {
  const dosEnUno = dto.type === 'TWO_IN_ONE';

  return {
    id: String(dto.id),
    tipo: dtoATipoOferta(dto.type),
    producto: {
      id: String(dto.productId ?? ''),
      nombre: dto.productName,
      presentacion: '',
      compra: dto.buyQuantity ?? 0,
      valor: aNumero(dosEnUno ? dto.offerPrice : dto.getQuantity),
    },
    periodo: dtoAPeriodo(dto.period),
    cantidadMaxima: dto.maxQuantity ?? 0,
    stock: dto.stock ?? 0,
    sucursales: dto.branches.map(dtoASucursalOferta),
  };
}

export function precioBajoADto(form: PrecioBajoFormValue): LowPriceRequestDto {
  return {
    period: periodoADto(form.periodo),
    appliesOnline: form.menuOnline,
    rangeBased: form.porRango,
    products: form.productos.map((p) => ({
      productId: Number(p.id),
      presentationId: null,
      productName: p.nombre,
      offerPrice: p.precio,
      minQuantity: form.porRango ? p.cantidadMinima : null,
      maxQuantity: form.porRango ? p.cantidadMaxima : null,
    })),
    branches: form.sucursales.map(sucursalOfertaADto),
  };
}

export function dtoAPrecioBajo(dto: LowPriceResponseDto): OfertaPrecioBajo {
  return {
    id: String(dto.id),
    producto: {
      id: String(dto.productId ?? ''),
      nombre: dto.productName,
      presentacion: '',
      precio: aNumero(dto.offerPrice),
      cantidadMinima: dto.minQuantity ?? 0,
      cantidadMaxima: dto.maxQuantity ?? 0,
    },
    periodo: dtoAPeriodo(dto.period),
    menuOnline: dto.appliesOnline,
    porRango: dto.rangeBased,
    stock: dto.stock ?? 0,
    version: dto.priceVersion ?? '',
    sucursales: dto.branches.map(dtoASucursalOferta),
  };
}
